import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { motion, useScroll, useTransform, useMotionValue } from "motion/react";
import React from "react";
import {
  lazy,
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { parseBML } from "@/lib/bml-parser";
import { renderBML } from "@/lib/bml-renderer";
import type { Monaco } from "@monaco-editor/react";
import type * as monaco from "monaco-editor";
const MonacoEditor = lazy(() => import("@monaco-editor/react"));
import { useSession, embeddedAwareSignOut } from "@/lib/auth-client";
import { createBioDraft } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import TiltedDiv from "@/components/TiltedDiv";
import {
  generateOrganizationSchema,
  generateWebSiteSchema,
  generateWebPageSchema,
} from "@/lib/structured-data";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  User,
  Settings,
  LogOut,
  LayoutPanelLeft,
  Menu,
  X,
  LogIn,
  Loader2,
  Sparkles,
  Heart,
  Star,
  Flower,
  Moon,
  Sun,
  Rainbow,
  Zap,
  Gem,
  Crown,
  Code2,
  Eye,
  Palette,
  Link,
  TerminalSquare,
} from "lucide-react";
import { Helmet } from "react-helmet-async";

export const Route = createFileRoute("/")({
  component: LandingPage,
});

function FloatingBubble({ delay = 0, size = 60, left = "20%", top = "30%" }) {
  return (
    <motion.div
      className="absolute rounded-full backdrop-blur-sm"
      style={{
        left,
        top,
        width: size,
        height: size,
        background: "rgba(147, 197, 253, 0.18)",
        border: "1px solid rgba(255,255,255,0.5)",
        boxShadow:
          "inset 0 6px 12px rgba(255,255,255,0.6), 0 0 16px rgba(147, 197, 253, 0.3)",
      }}
      animate={{
        y: [0, -30, 0],
        x: [0, 15, -15, 0],
        scale: [1, 1.1, 0.9, 1],
      }}
      transition={{
        duration: 8,
        delay,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />
  );
}

function KawaiiCloud({ delay = 0, left = "10%", top = "20%" }) {
  return (
    <motion.svg
      width="120"
      height="60"
      viewBox="0 0 120 60"
      className="absolute"
      style={{ left, top }}
      animate={{
        x: [0, 30, 0],
        y: [0, -10, 0],
      }}
      transition={{
        duration: 15,
        delay,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    >
      <g opacity="0.6">
        <ellipse cx="30" cy="40" rx="25" ry="20" fill="url(#cloudGradient)" />
        <ellipse cx="55" cy="35" rx="30" ry="25" fill="url(#cloudGradient)" />
        <ellipse cx="80" cy="40" rx="25" ry="20" fill="url(#cloudGradient)" />
        <rect x="20" y="40" width="65" height="15" fill="url(#cloudGradient)" />
      </g>
      <defs>
        <linearGradient id="cloudGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="rgb(255, 240, 245)" stopOpacity="0.8" />
          <stop
            offset="100%"
            stopColor="rgb(255, 228, 240)"
            stopOpacity="0.6"
          />
        </linearGradient>
      </defs>
    </motion.svg>
  );
}

function AnimatedSparkle({ delay = 0, left = "50%", top = "50%", size = 24 }) {
  return (
    <motion.div
      className="absolute"
      style={{ left, top }}
      animate={{
        scale: [0, 1, 0],
        rotate: [0, 180, 360],
        opacity: [0, 1, 0],
      }}
      transition={{
        duration: 3,
        delay,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    >
      <Sparkles size={size} className="text-accent" />
    </motion.div>
  );
}

export function FigureEight({
  amplitudeX = 40,
  amplitudeY = 24,
  duration = 6,
  phase = 0,
  reverse = false,
  className = "",
  children,
}: {
  amplitudeX?: number;
  amplitudeY?: number;
  duration?: number;
  phase?: number;
  reverse?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  // simple raf loop for smooth parametric animation
  // x = a * sin(t + phase)
  // y = b * sin(2 * (t + phase)) with optional reverse multiplier
  React.useEffect(() => {
    let raf = 0;
    const start = performance.now();
    const loop = (now: number) => {
      const elapsed = (now - start) / 1000;
      const t = ((elapsed % duration) / duration) * Math.PI * 2 + phase;
      const dir = reverse ? -1 : 1;
      x.set(amplitudeX * Math.sin(t));
      y.set(dir * amplitudeY * Math.sin(2 * t));
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [amplitudeX, amplitudeY, duration, phase, reverse, x, y]);

  return (
    <motion.div
      className={
        "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 " +
        className
      }
      style={{ x, y }}
    >
      {children}
    </motion.div>
  );
}

type BurstItem = {
  id: string;
  angle: number;
  radius: number;
  size: number;
  delay: number;
  orbit: number;
  node: React.ReactNode;
};

// the firework burst should only play on the very first mount. on any later
// remount (strict-mode double invoke, route re-entry, auth re-render) we skip
// the hidden initial state so the orbiting symbols never blink out of view.
let heroBurstPlayed = false;

function HeroVisual() {
  // one-shot firework burst: every shape is launched from the center out
  // along a straight radial line, then settles. no looping re-fire.
  const [playBurst] = useState(() => {
    if (heroBurstPlayed) return false;
    heroBurstPlayed = true;
    return true;
  });
  const burstDuration = 1;
  const burstEase = [0.16, 1, 0.3, 1] as const;

  const baseRadius =
    typeof window !== "undefined" && window.innerWidth < 640
      ? 120
      : window.innerWidth < 768
        ? 158
        : 198;

  // core is sized from the same scale as the symbols so every orbit radius
  // stays clear of it on every breakpoint (no symbols ducking behind the glow)
  const coreRadius = baseRadius * 0.55;
  const coreSize = coreRadius * 2;

  const orbColors = [
    "rgb(251, 207, 232)",
    "rgb(147, 197, 253)",
    "rgb(254, 240, 138)",
    "rgb(252, 211, 77)",
  ];

  const outerIcons = [
    { icon: Star, color: "text-yellow-300", size: 32 },
    { icon: Heart, color: "text-pink-300", size: 28 },
    { icon: Flower, color: "text-purple-300", size: 30 },
    { icon: Moon, color: "text-blue-300", size: 26 },
    { icon: Sun, color: "text-orange-300", size: 34 },
    { icon: Rainbow, color: "text-green-300", size: 28 },
  ];

  const middleIcons = [
    { icon: Zap, color: "text-accent" },
    { icon: Gem, color: "text-accent" },
    { icon: Crown, color: "text-accent" },
    { icon: Sparkles, color: "text-accent" },
  ];

  const items: BurstItem[] = [
    ...outerIcons.map((o, i) => ({
      id: `outer-${i}`,
      angle: (i / outerIcons.length) * 360,
      radius: baseRadius,
      size: o.size,
      delay: i * 0.05,
      orbit: 50,
      node: (
        <o.icon
          size={o.size}
          className={`${o.color} drop-shadow-[0_0_12px_currentColor]`}
        />
      ),
    })),
    ...middleIcons.map((o, i) => ({
      id: `mid-${i}`,
      angle: (i / middleIcons.length) * 360 + 45,
      radius: baseRadius * 0.8,
      size: 24,
      delay: 0.1 + i * 0.05,
      orbit: 32,
      node: <o.icon size={24} className={`${o.color}/70`} />,
    })),
    ...Array.from({ length: 15 }).map((_, i) => {
      const color = orbColors[i % orbColors.length];
      return {
        id: `orb-${i}`,
        angle: (i / 15) * 360 + (i % 3) * 12,
        radius: baseRadius * (0.7 + (i % 3) * 0.16),
        size: 12,
        delay: 0.05 + (i % 5) * 0.04,
        orbit: 38,
        node: (
          <div
            className="w-3 h-3 rounded-full"
            style={{
              background: `linear-gradient(45deg, ${color}80, ${color}40)`,
              boxShadow: `0 0 8px ${color}80`,
            }}
          />
        ),
      };
    }),
    ...Array.from({ length: 8 }).map((_, i) => ({
      id: `particle-${i}`,
      angle: (i / 8) * 360 + 22,
      radius: baseRadius * 1.06,
      size: 24,
      delay: 0.08 + i * 0.03,
      orbit: 44,
      node: (
        <img
          src="/icon.png"
          alt=""
          className="w-full h-full object-contain drop-shadow"
        />
      ),
    })),
  ].map((it, i) => ({ ...it, delay: i * 0.075 }));

  return (
    <div className="relative w-full h-[400px] sm:h-[500px] md:h-[600px] flex items-center justify-center">
      {/* straight radial trails drawn from the center outward (firework streaks) */}
      {items.map((item) => (
        <motion.div
          key={`trail-${item.id}`}
          className="absolute left-1/2 top-1/2"
          style={{ rotate: `${item.angle}deg` }}
        >
          <motion.div
            className="absolute left-0 top-0 h-px origin-left -translate-y-1/2 rounded-full"
            initial={playBurst ? { width: 0, opacity: 0 } : false}
            animate={{ width: item.radius, opacity: [0, 0.7, 0] }}
            transition={{
              duration: burstDuration,
              ease: burstEase,
              delay: item.delay,
            }}
          >
            <div className="h-full w-full rounded-full bg-gradient-to-r from-transparent via-accent/40 to-accent/70" />
          </motion.div>
        </motion.div>
      ))}

      {/* each shape fires once from the center along its radial line, then
          its whole arm rotates to orbit around the core (no re-fire) */}
      {items.map((item) => (
        <motion.div
          key={item.id}
          className="absolute left-1/2 top-1/2"
          initial={playBurst ? { rotate: item.angle } : false}
          animate={{ rotate: item.angle + 360 }}
          transition={{
            duration: item.orbit,
            repeat: Infinity,
            ease: "linear",
            delay: item.delay + burstDuration,
          }}
        >
          <motion.div
            className="absolute left-0 top-0"
            initial={playBurst ? { x: 0, scale: 0, opacity: 0 } : false}
            animate={{ x: item.radius, scale: 1, opacity: 1 }}
            transition={{
              duration: burstDuration,
              ease: burstEase,
              delay: item.delay,
            }}
          >
            <div className="absolute -translate-x-1/2 -translate-y-1/2">
              <motion.div
                animate={{ opacity: [1, 0.55, 1], scale: [1, 1.1, 1] }}
                transition={{
                  duration: 2.4,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: item.delay + burstDuration,
                }}
              >
                {item.node}
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      ))}

      {/* central core (gentle ambient life, not part of the one-shot burst) */}
      <motion.div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-br from-accent/40 via-primary/40 to-secondary/40 backdrop-blur-xl border-4 border-white/50 shadow-2xl flex items-center justify-center"
        style={{ width: coreSize, height: coreSize }}
        animate={{
          scale: [1, 1.06, 1],
          boxShadow: [
            "0 0 60px rgba(147, 197, 253, 0.4)",
            "0 0 100px rgba(147, 197, 253, 0.6)",
            "0 0 60px rgba(147, 197, 253, 0.4)",
          ],
        }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      >
        <motion.div
          className="relative"
          animate={{ rotate: 360 }}
          transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
        >
          <div className="relative w-32 h-32 flex items-center justify-center">
            <FigureEight amplitudeX={64} amplitudeY={22} duration={6} phase={0}>
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-300/30 to-purple-300/30 backdrop-blur-sm flex items-center justify-center">
                <Heart size={24} className="text-pink-300/80" />
              </div>
            </FigureEight>

            <FigureEight
              amplitudeX={52}
              amplitudeY={20}
              duration={5.5}
              phase={2.094}
              reverse
            >
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-yellow-300/30 to-orange-300/30 backdrop-blur-sm flex items-center justify-center">
                <Star size={20} className="text-yellow-300/80" />
              </div>
            </FigureEight>

            <FigureEight
              amplitudeX={45}
              amplitudeY={18}
              duration={6.5}
              phase={4.188}
            >
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-300/30 to-blue-300/30 backdrop-blur-sm flex items-center justify-center">
                <Flower size={22} className="text-purple-300/80" />
              </div>
            </FigureEight>

            <motion.div
              className="relative z-10"
              animate={{
                scale: [1, 1.05, 1],
                filter: [
                  "drop-shadow(0 0 20px rgba(147, 197, 253, 0.5))",
                  "drop-shadow(0 0 40px rgba(147, 197, 253, 0.8))",
                  "drop-shadow(0 0 20px rgba(147, 197, 253, 0.5))",
                ],
              }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <img
                src="/icon.png"
                alt="clove.moe"
                className="w-20 h-20 object-contain drop-shadow-lg"
              />
            </motion.div>

            <motion.div
              className="absolute inset-0 rounded-full border-2 border-accent/40"
              animate={{ scale: [0.8, 1.2, 0.8], opacity: [0.8, 0, 0.8] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <motion.div
              className="absolute inset-0 rounded-full border border-primary/30"
              animate={{ scale: [0.6, 1.4, 0.6], opacity: [0.6, 0, 0.6] }}
              transition={{ duration: 2.5, repeat: Infinity, delay: 0.5 }}
            />

            <motion.div
              className="absolute -top-2 -right-2"
              animate={{
                scale: [0, 1, 0],
                rotate: [0, 180, 360],
                opacity: [0, 1, 0],
              }}
              transition={{ duration: 2, repeat: Infinity, delay: 0 }}
            >
              <Sparkles size={16} className="text-accent" />
            </motion.div>
            <motion.div
              className="absolute -bottom-2 -left-2"
              animate={{
                scale: [0, 1, 0],
                rotate: [360, 180, 0],
                opacity: [0, 1, 0],
              }}
              transition={{ duration: 2, repeat: Infinity, delay: 1 }}
            >
              <Sparkles size={14} className="text-primary" />
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}

function LandingPage() {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const url = typeof window !== "undefined" ? window.location.href : "";
  const organizationJsonLd = JSON.stringify(generateOrganizationSchema());
  const webSiteJsonLd = JSON.stringify(generateWebSiteSchema());
  const landingPageJsonLd = JSON.stringify(
    generateWebPageSchema(
      "WebPage",
      "clove.moe - free bio link page builder with custom markup",
      "create beautiful custom bio pages and link-in-bio pages for free. use simple bml markup or custom html/css. no ads, no limits. the best free alternative to linktree, beacons, and carrd.",
      "https://clove.moe/",
    ),
  );
  const { data: session } = useSession();
  const [showPreview, setShowPreview] = useState(true);
  const [showNav, setShowNav] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const closeMenu = () => setMobileMenuOpen(false);

  const startCreating = async () => {
    try {
      const res = await createBioDraft({});
      if (res.status === 401) {
        window.location.href = "/sign-in";
        return;
      }
      if (!res.ok) return;
      const data = await res.json();
      if (data?.id)
        window.location.href = `/editor/${encodeURIComponent(data.id)}`;
    } catch {
      // ignore network errors
    }
  };
  const heroRef = useRef<HTMLDivElement>(null);
  const heroLogoRef = useRef<HTMLImageElement>(null);
  const heroPrimaryRef = useRef<HTMLElement | null>(null);
  const heroSecondaryRef = useRef<HTMLElement | null>(null);
  const navLogoRef = useRef<HTMLImageElement>(null);
  const navPrimaryRef = useRef<HTMLElement | null>(null);
  const navSecondaryRef = useRef<HTMLElement | null>(null);
  const hasMountedRef = useRef(false);
  const prevShowNavRef = useRef(showNav);

  const sampleBml = ` {/* edit me! */}
  <Bio>
  <Header>
    <Name className="text-4xl">♡ luna ♡</Name>
    <Title>just a cutie building things</Title>
  </Header>
  <Section title="about me ✧">
    <Text variant="muted">i love cats, code, and bubble tea</Text>
  </Section>
  <Section title="find me ~">
    <LinkGrid>
      <Link href="#">twitter</Link>
      <Link href="#">github</Link>
    </LinkGrid>
  </Section>
</Bio>`;

  const [bmlText, setBmlText] = useState(sampleBml);
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<Monaco | null>(null);

  const { scrollYProgress } = useScroll();
  const yRange = useTransform(scrollYProgress, [0, 0.2], [0, 1]);
  const opacity = useTransform(yRange, [0, 1], [1, 0]);
  const scale = useTransform(yRange, [0, 1], [1, 0.8]);

  useEffect(() => {
    const onScroll = () => {
      const next = window.scrollY > 100;
      setShowNav((prev) => (prev === next ? prev : next));
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useLayoutEffect(() => {
    const heroLogo = heroLogoRef.current;
    const navLogo = navLogoRef.current;
    if (!heroLogo || !navLogo) return;

    const heroPrimary = heroPrimaryRef.current;
    const navPrimary = navPrimaryRef.current;
    const heroSecondary = heroSecondaryRef.current;
    const navSecondary = navSecondaryRef.current;

    const ease = "cubic-bezier(0.22, 1, 0.36, 1)";

    const morph = (fromEl: HTMLElement | null, toEl: HTMLElement | null) => {
      if (!fromEl || !toEl) return;
      const from = fromEl.getBoundingClientRect();
      const to = toEl.getBoundingClientRect();
      if (!from.width || !from.height || !to.width || !to.height) return;

      const dx = from.left - to.left;
      const dy = from.top - to.top;
      const sx = from.width / to.width;
      const sy = from.height / to.height;

      const previousTransition = toEl.style.transition;
      toEl.style.transition = "none";
      toEl.style.willChange = "transform";
      toEl.style.setProperty("transform-box", "border-box");
      toEl.style.transformOrigin = "0 0";
      toEl.style.backfaceVisibility = "hidden";
      toEl.style.transform = `translate3d(${dx}px, ${dy}px, 0) scale(${sx}, ${sy})`;

      void toEl.getBoundingClientRect();

      toEl.style.transition = `transform 500ms ${ease}`;
      toEl.style.transform = "translate3d(0,0,0) scale(1,1)";
      const cleanup = () => {
        toEl.style.transition = previousTransition;
        toEl.style.willChange = "";
        toEl.style.removeProperty("transform-box");
        toEl.style.backfaceVisibility = "";
        toEl.removeEventListener("transitionend", cleanup);
      };
      toEl.addEventListener("transitionend", cleanup);
    };

    const setOpacity = (elements: Array<HTMLElement | null>, value: string) => {
      elements.forEach((element) => {
        if (element) element.style.opacity = value;
      });
    };

    if (!hasMountedRef.current) {
      setOpacity([heroLogo, heroPrimary, heroSecondary], showNav ? "0" : "1");
      setOpacity([navLogo, navPrimary, navSecondary], showNav ? "1" : "0");
      hasMountedRef.current = true;
      prevShowNavRef.current = showNav;
      return;
    }

    if (prevShowNavRef.current === showNav) {
      setOpacity([heroLogo, heroPrimary, heroSecondary], showNav ? "0" : "1");
      setOpacity([navLogo, navPrimary, navSecondary], showNav ? "1" : "0");
      return;
    }

    if (showNav) {
      setOpacity([heroLogo, heroPrimary, heroSecondary], "0");
      setOpacity([navLogo, navPrimary, navSecondary], "1");

      morph(heroLogo, navLogo);
      morph(heroPrimary, navPrimary);
      morph(heroSecondary, navSecondary);
    } else {
      setOpacity([navLogo, navPrimary, navSecondary], "0");
      setOpacity([heroLogo, heroPrimary, heroSecondary], "1");

      morph(navLogo, heroLogo);
      morph(navPrimary, heroPrimary);
      morph(navSecondary, heroSecondary);
    }

    prevShowNavRef.current = showNav;
  }, [showNav, session]);

  const handleBeforeMount = (monacoApi: Monaco) => {
    void import("@/lib/monaco-bml")
      .then(({ registerBmlLanguage }) => {
        registerBmlLanguage(
          monacoApi as unknown as typeof import("monaco-editor"),
        );
      })
      .catch(() => {});

    monacoApi.editor.defineTheme("bml-light", {
      base: "vs",
      inherit: false,
      rules: [
        { token: "custom-tag", foreground: "#d97706", fontStyle: "bold" },
        { token: "tag.bml", foreground: "#ea580c" },
        { token: "attribute.name.bml", foreground: "#dc2626" },
        { token: "delimiter.html", foreground: "#6b7280" },
        { token: "comment", foreground: "#9ca3af", fontStyle: "italic" },
        {
          token: "comment.content",
          foreground: "#9ca3af",
          fontStyle: "italic",
        },
        { token: "keyword", foreground: "#7c3aed" },
        { token: "number", foreground: "#0891b2" },
        { token: "string", foreground: "#16a34a" },
        { token: "expression.identifier", foreground: "#b45309" },
        { token: "operator", foreground: "#92400e" },
        { token: "delimiter", foreground: "#64748b" },
      ],
      colors: {
        "editor.background": "#FFFFFF",
        "editor.foreground": "#24292E",
        "editor.selectionBackground": "#0366D640",
        "editor.lineHighlightBackground": "#F6F8FA",
        "editorCursor.foreground": "#0366D6",
        "editorWhitespace.foreground": "#D1D5DA",
      },
    });
    monacoApi.editor.setTheme("bml-light");
  };

  const handleEditorDidMount = (
    ed: monaco.editor.IStandaloneCodeEditor,
    monacoApi: Monaco,
  ) => {
    editorRef.current = ed;
    monacoRef.current = monacoApi;
    const model = ed.getModel();
    if (model) {
      const m = monacoApi as unknown as typeof import("monaco-editor");
      m.editor.setModelLanguage(model as any, "bml");
    }
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <Helmet>
        <title>
          clove.moe - free bio link page builder with custom markup | better
          than linktree
        </title>
        <meta
          name="description"
          content="create beautiful custom bio pages and link-in-bio pages for free. use simple bml markup or custom html/css. no ads, no limits. the best free alternative to linktree, beacons, and carrd."
        />
        <meta
          name="keywords"
          content="bio link, link in bio, linktree alternative, free bio page, custom bio page, personal homepage, bio link page builder, link tree free, beacons alternative, carrd alternative, custom markup, html css bio page, social media links, personal website builder"
        />
        <link rel="canonical" href="https://clove.moe/" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={url} />
        <meta
          property="og:title"
          content="clove.moe - free custom bio link page builder"
        />
        <meta
          property="og:description"
          content="create beautiful custom bio pages with our free link-in-bio builder. use simple markup or full html/css. better than linktree. no ads, unlimited links, forever free."
        />
        <meta property="og:image" content={`${origin}/icon.png`} />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta property="og:image:alt" content="clove.moe logo" />
        <meta property="og:site_name" content="clove.moe" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={url} />
        <meta
          name="twitter:title"
          content="clove.moe - free custom bio link page builder"
        />
        <meta
          name="twitter:description"
          content="create beautiful custom bio pages for free. simple markup or full html/css. better than linktree. no ads, no limits, forever free."
        />
        <meta name="twitter:image" content={`${origin}/icon.png`} />
        <meta name="twitter:image:alt" content="clove.moe logo" />

        <meta name="author" content="clove.moe" />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />
        <meta httpEquiv="content-language" content="en-US" />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: organizationJsonLd }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: webSiteJsonLd }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: landingPageJsonLd }}
        />
      </Helmet>

      <nav
        aria-label="primary"
        className={`fixed inset-x-0 top-0 z-50 px-3 sm:px-4 mt-3 sm:mt-4 transition-opacity duration-300 ${
          showNav
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="mx-auto max-w-7xl">
          <div className="relative z-50 flex items-center justify-between gap-2 rounded-full border border-accent/40 bg-card/90 backdrop-blur-xl px-3 sm:px-4 py-2 sm:py-2.5 shadow-lg shadow-accent/20">
            <a href="#" className="flex items-center gap-2 shrink-0">
              <img
                ref={navLogoRef}
                src="/header-logo.png"
                alt="clove.moe"
                className="h-6 sm:h-8 w-auto"
              />
            </a>

            <div className="hidden sm:flex items-center gap-1.5 md:gap-2">
              <a
                href="#features"
                className="text-foreground/70 hover:text-foreground px-2 md:px-3 py-1 rounded-full hover:bg-black/5 transition-colors text-sm md:text-base whitespace-nowrap"
              >
                features
              </a>
              <a
                href="/docs#welcome"
                className="text-foreground/70 hover:text-foreground px-2 md:px-3 py-1 rounded-full hover:bg-black/5 transition-colors text-sm md:text-base whitespace-nowrap"
              >
                docs
              </a>
              <div className="ml-1 md:ml-2 flex items-center gap-1.5 md:gap-2">
                {session ? (
                  <>
                    <Button
                      asChild
                      size="sm"
                      className="bg-accent text-accent-foreground font-semibold px-2.5 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded-full transition-opacity duration-300 text-xs sm:text-sm whitespace-nowrap"
                    >
                      <a
                        ref={(el) => {
                          navPrimaryRef.current = el;
                        }}
                        href="/dashboard"
                        className="transition-opacity duration-300"
                      >
                        dashboard ♡
                      </a>
                    </Button>
                    <Button
                      asChild
                      variant="outline"
                      size="sm"
                      className="border-border rounded-full transition-opacity duration-300 px-2.5 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 text-xs sm:text-sm whitespace-nowrap"
                    >
                      <a
                        ref={(el) => {
                          navSecondaryRef.current = el;
                        }}
                        href="/editor/new"
                        className="transition-opacity duration-300"
                      >
                        editor
                      </a>
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="rounded-full p-1"
                        >
                          <Avatar className="h-6 w-6 sm:h-7 sm:w-7">
                            <AvatarImage
                              src={session.user.image || undefined}
                            />
                            <AvatarFallback>
                              {session.user.name?.charAt(0) || "?"}
                            </AvatarFallback>
                          </Avatar>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem
                          onClick={() =>
                            (window.location.href = `/profile/${session.user.username}`)
                          }
                        >
                          <User className="mr-2 h-4 w-4" />
                          profile
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => (window.location.href = "/dashboard")}
                        >
                          <LayoutPanelLeft className="mr-2 h-4 w-4" />
                          dashboard
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => (window.location.href = "/settings")}
                        >
                          <Settings className="mr-2 h-4 w-4" />
                          settings
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => embeddedAwareSignOut()}
                          className="text-red-600"
                        >
                          <LogOut className="mr-2 h-4 w-4" />
                          sign out
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                ) : (
                  <>
                    <Button
                      size="sm"
                      className="bg-accent text-accent-foreground font-semibold px-2.5 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded-full transition-opacity duration-300 text-xs sm:text-sm whitespace-nowrap"
                      ref={(el) => {
                        navPrimaryRef.current = el;
                      }}
                      onClick={startCreating}
                    >
                      start creating ♡
                    </Button>
                    <Button
                      asChild
                      variant="outline"
                      size="sm"
                      className="border-border rounded-full transition-opacity duration-300 px-2.5 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 text-xs sm:text-sm whitespace-nowrap"
                    >
                      <a
                        ref={(el) => {
                          navSecondaryRef.current = el;
                        }}
                        href="/sign-in"
                        className="transition-opacity duration-300"
                      >
                        sign in
                      </a>
                    </Button>
                  </>
                )}
              </div>
            </div>

            <div className="sm:hidden">
              <Button
                variant="ghost"
                size="sm"
                className="rounded-full p-2"
                aria-label={mobileMenuOpen ? "close menu" : "open menu"}
                aria-expanded={mobileMenuOpen}
                onClick={() => setMobileMenuOpen((o) => !o)}
              >
                {mobileMenuOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
              </Button>
            </div>
          </div>

          {mobileMenuOpen && (
            <>
              <div
                className="sm:hidden fixed inset-0 z-40"
                aria-hidden="true"
                onClick={closeMenu}
              />
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="sm:hidden relative z-50 mt-2 rounded-2xl border border-accent/40 bg-card/95 backdrop-blur-xl shadow-lg shadow-accent/20 p-2"
              >
                <div className="flex flex-col">
                  <a
                    href="#features"
                    onClick={closeMenu}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-foreground/80 hover:text-foreground hover:bg-black/5 transition-colors text-sm"
                  >
                    features
                  </a>
                  <a
                    href="/docs#welcome"
                    onClick={closeMenu}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-foreground/80 hover:text-foreground hover:bg-black/5 transition-colors text-sm"
                  >
                    docs
                  </a>
                  <div className="my-1 h-px bg-border/60" />
                  {session ? (
                    <>
                      <a
                        href={`/profile/${session.user.username}`}
                        onClick={closeMenu}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-foreground/80 hover:text-foreground hover:bg-black/5 transition-colors text-sm"
                      >
                        <User className="h-4 w-4" />
                        profile
                      </a>
                      <a
                        href="/dashboard"
                        onClick={closeMenu}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-foreground/80 hover:text-foreground hover:bg-black/5 transition-colors text-sm"
                      >
                        <LayoutPanelLeft className="h-4 w-4" />
                        dashboard
                      </a>
                      <a
                        href="/editor/new"
                        onClick={closeMenu}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-foreground/80 hover:text-foreground hover:bg-black/5 transition-colors text-sm"
                      >
                        <Code2 className="h-4 w-4" />
                        editor
                      </a>
                      <a
                        href="/settings"
                        onClick={closeMenu}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-foreground/80 hover:text-foreground hover:bg-black/5 transition-colors text-sm"
                      >
                        <Settings className="h-4 w-4" />
                        settings
                      </a>
                      <button
                        type="button"
                        onClick={() => {
                          closeMenu();
                          embeddedAwareSignOut();
                        }}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-600 hover:bg-red-500/10 transition-colors text-sm text-left w-full"
                      >
                        <LogOut className="h-4 w-4" />
                        sign out
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          closeMenu();
                          startCreating();
                        }}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-accent text-accent-foreground font-semibold hover:bg-accent/90 transition-colors text-sm w-full"
                      >
                        <Sparkles className="h-4 w-4" />
                        start creating ♡
                      </button>
                      <a
                        href="/sign-in"
                        onClick={closeMenu}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-foreground/80 hover:text-foreground hover:bg-black/5 transition-colors text-sm"
                      >
                        <LogIn className="h-4 w-4" />
                        sign in
                      </a>
                    </>
                  )}
                </div>
              </motion.div>
            </>
          )}
        </div>
      </nav>

      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 20% 30%, rgba(147, 197, 253, 0.15), transparent 50%), radial-gradient(circle at 80% 70%, rgba(251, 207, 232, 0.15), transparent 50%)",
          }}
          animate={{
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        <FloatingBubble delay={0} size={80} left="10%" top="20%" />
        <FloatingBubble delay={2} size={60} left="85%" top="30%" />
        <FloatingBubble delay={4} size={100} left="70%" top="70%" />
        <FloatingBubble delay={1} size={50} left="25%" top="80%" />

        <KawaiiCloud delay={0} left="5%" top="15%" />
        <KawaiiCloud delay={3} left="70%" top="10%" />
        <KawaiiCloud delay={6} left="40%" top="85%" />

        <AnimatedSparkle delay={0} left="15%" top="25%" size={20} />
        <AnimatedSparkle delay={1} left="80%" top="40%" size={24} />
        <AnimatedSparkle delay={2} left="30%" top="70%" size={18} />
        <AnimatedSparkle delay={1.5} left="90%" top="80%" size={22} />
      </div>

      <motion.section
        ref={heroRef}
        className="relative z-10 min-h-screen flex items-center justify-center px-4 sm:px-6"
        style={{ opacity, scale }}
        aria-label="hero"
      >
        <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center py-16 sm:py-24 lg:py-8">
          <motion.div
            className="order-2 lg:order-2 flex justify-center"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <HeroVisual />
          </motion.div>

          <motion.div
            className="order-1 lg:order-2 text-center lg:text-left pt-16 sm:pt-0"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <img
              ref={heroLogoRef}
              src="/header-logo.png"
              alt="clove.moe logo - free bio link page builder"
              className="h-20 sm:h-28 md:h-32 w-auto mb-6 sm:mb-8 mx-auto lg:mx-0"
              width="auto"
              height="auto"
            />

            <motion.h1
              className="text-2xl sm:text-3xl lg:text-4xl text-foreground/90 mb-6 sm:mb-8 leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              the <span className="text-accent font-bold">cutest</span> way to
              make a little homepage. with{" "}
              <span className="text-accent font-bold">custom</span> markup
              language. all free.{" "}
              <span className="text-accent font-bold">forever</span>. ♡
            </motion.h1>

            <motion.div
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              {session ? (
                <>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-full sm:w-auto"
                  >
                    <Button
                      asChild
                      size="lg"
                      className="w-full sm:w-auto bg-accent text-accent-foreground font-semibold px-6 sm:px-8 py-6 sm:py-7 rounded-full shadow-lg shadow-accent/30 text-base sm:text-lg"
                    >
                      <a
                        ref={(el) => {
                          heroPrimaryRef.current = el;
                        }}
                        href="/dashboard"
                      >
                        dashboard ♡
                      </a>
                    </Button>
                  </motion.div>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-full sm:w-auto"
                  >
                    <Button
                      asChild
                      variant="outline"
                      size="lg"
                      className="w-full sm:w-auto border-border bg-card/80 px-6 sm:px-8 py-6 sm:py-7 rounded-full text-base sm:text-lg"
                    >
                      <a
                        ref={(el) => {
                          heroSecondaryRef.current = el;
                        }}
                        href="/editor/new"
                      >
                        editor
                      </a>
                    </Button>
                  </motion.div>
                </>
              ) : (
                <>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-full sm:w-auto"
                  >
                    <Button
                      size="lg"
                      className="w-full sm:w-auto bg-accent text-accent-foreground font-semibold px-6 sm:px-8 py-6 sm:py-7 rounded-full shadow-lg shadow-accent/30 text-base sm:text-lg transition-opacity duration-300"
                      ref={(el) => {
                        heroPrimaryRef.current = el;
                      }}
                      onClick={async () => {
                        try {
                          const res = await createBioDraft({});
                          if (res.status === 401) {
                            window.location.href = "/sign-in";
                            return;
                          }
                          if (!res.ok) return;
                          const data = await res.json();
                          if (data?.id)
                            window.location.href = `/editor/${encodeURIComponent(
                              data.id,
                            )}`;
                        } catch {
                          // ignore network errors
                        }
                      }}
                    >
                      start creating ♡
                    </Button>
                  </motion.div>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-full sm:w-auto"
                  >
                    <Button
                      asChild
                      variant="outline"
                      size="lg"
                      className="w-full sm:w-auto border-border bg-card/80 px-6 sm:px-8 py-6 sm:py-7 rounded-full text-base sm:text-lg transition-opacity duration-300"
                    >
                      <a
                        ref={(el) => {
                          heroSecondaryRef.current = el;
                        }}
                        href="/sign-in"
                        className="transition-opacity duration-300"
                      >
                        sign in
                      </a>
                    </Button>
                  </motion.div>
                </>
              )}
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      <section
        className="relative z-10 py-16 sm:py-24 lg:py-32 px-4 sm:px-6"
        aria-labelledby="editor-heading"
      >
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2
              id="editor-heading"
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-3 sm:mb-4"
            >
              see it in action ✧
            </h2>
            <p className="text-lg sm:text-xl text-foreground/70">
              write it, see it, love it. all in one cute little editor.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <TiltedDiv scaleOnHover={1} rotateAmplitude={0.5}>
              <div className="bg-card/60 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 border border-border/40 shadow-2xl">
                <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                  <div className="w-4 h-4 bg-red-300 rounded-full" />
                  <div className="w-4 h-4 bg-yellow-300 rounded-full" />
                  <div className="w-4 h-4 bg-green-300 rounded-full" />
                  <div className="ml-2 sm:ml-4 text-accent text-xs sm:text-sm font-medium">
                    ~/clove-moe/editor
                  </div>
                </div>

                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <div className="inline-flex rounded-xl sm:rounded-2xl border border-border/40 bg-background/50 backdrop-blur p-0.5 sm:p-1">
                    <motion.button
                      className={`px-3 sm:px-4 py-1.5 sm:py-2 text-xs rounded-lg sm:rounded-xl transition-all ${
                        !showPreview
                          ? "bg-accent/20 text-accent"
                          : "text-foreground/70"
                      }`}
                      onClick={() => setShowPreview(false)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      markup
                    </motion.button>
                    <motion.button
                      className={`px-3 sm:px-4 py-1.5 sm:py-2 text-xs rounded-lg sm:rounded-xl transition-all ${
                        showPreview
                          ? "bg-accent/20 text-accent"
                          : "text-foreground/70"
                      }`}
                      onClick={() => setShowPreview(true)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      preview
                    </motion.button>
                  </div>
                </div>

                {!showPreview ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="bg-background/50 border border-border/60 rounded-xl sm:rounded-2xl p-1 sm:p-2"
                  >
                    <Suspense
                      fallback={
                        <div className="h-[240px] sm:h-[280px] md:h-[320px] flex items-center justify-center">
                          <Loader2 className="animate-spin" />
                        </div>
                      }
                    >
                      <MonacoEditor
                        height={
                          typeof window !== "undefined" &&
                          window.innerWidth < 640
                            ? "240px"
                            : window.innerWidth < 768
                              ? "280px"
                              : "320px"
                        }
                        defaultLanguage="bml"
                        path="bml://landing-sample.bml"
                        keepCurrentModel
                        value={bmlText}
                        onChange={(v) => setBmlText(v || "")}
                        beforeMount={handleBeforeMount}
                        onMount={handleEditorDidMount}
                        theme="bml-light"
                        options={{
                          minimap: { enabled: false },
                          fontSize:
                            typeof window !== "undefined" &&
                            window.innerWidth < 640
                              ? 11
                              : 13,
                          lineNumbers: "on",
                          wordWrap: "on",
                          automaticLayout: true,
                          renderLineHighlight: "none",
                          occurrencesHighlight: "off",
                          scrollBeyondLastLine: false,
                        }}
                      />
                    </Suspense>
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="bg-background/50 border border-border/60 rounded-xl sm:rounded-2xl p-3 sm:p-4"
                  >
                    {(() => {
                      const parsed = parseBML(bmlText);
                      if (parsed.error) {
                        return (
                          <div className="text-accent text-sm">
                            failed to parse bml –{" "}
                            {(parsed.error as Error).message}
                          </div>
                        );
                      }
                        return (
                          <div className="rounded-lg">
                            {renderBML(parsed.ast!)}
                          </div>
                        );
                      })()}
                  </motion.div>
                )}
              </div>
            </TiltedDiv>
          </motion.div>
        </div>
      </section>

      <section
        className="relative z-10 py-16 sm:py-24 lg:py-32 px-4 sm:px-6"
        id="features"
        aria-labelledby="features-heading"
      >
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2
              id="features-heading"
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-3 sm:mb-4"
            >
              why you'll love it ♡
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                title: "simple markup",
                desc: "our custom markup language, bml, is easy to learn and use. making cute pages has never been easier!",
                icon: Code2,
                color: "text-purple-400",
              },
              {
                title: "instant preview",
                desc: "see changes to your page in real-time as you edit your code. no more guessing what your page will look like.",
                icon: Eye,
                color: "text-blue-400",
              },
              {
                title: "free hosting, forever",
                desc: "your bio page is hosted on our fast and reliable servers on your very own link, completely free.",
                icon: Heart,
                color: "text-pink-400",
              },
              {
                title: "customize everything",
                desc: "make your page yours with inline styles in bml using tailwind or css with html for full control over the look and feel.",
                icon: Palette,
                color: "text-orange-400",
              },
              {
                title: "link management",
                desc: "easily add, remove, and organize links on your bio page to showcase your social media, projects, and more.",
                icon: Link,
                color: "text-green-400",
              },
              {
                title: "fully featured code editor",
                desc: "our built-in code editor comes with syntax highlighting, auto-completion, and error checking to make writing pages a breeze.",
                icon: TerminalSquare,
                color: "text-teal-400",
              },
            ].map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: idx * 0.1 }}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="bg-card/60 backdrop-blur-xl rounded-2xl p-6 sm:p-8 border border-border/40 shadow-lg"
                >
                  <div className={`mb-3 sm:mb-4 ${feature.color}`}>
                    <Icon
                      size={40}
                      className="sm:w-12 sm:h-12"
                      strokeWidth={1.5}
                    />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-foreground mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm sm:text-base text-foreground/70">
                    {feature.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section
        className="relative z-10 py-16 sm:py-24 lg:py-32 px-4 sm:px-6"
        aria-labelledby="cta-heading"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto text-center bg-gradient-to-br from-accent/20 via-primary/20 to-secondary/20 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-8 sm:p-12 lg:p-16 border border-accent/40 shadow-2xl"
        >
          <h2
            id="cta-heading"
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 sm:mb-6"
          >
            ready to create something cute?
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-foreground/70 mb-6 sm:mb-8">
            join thousands of creators making beautiful bio pages
          </p>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button
              size="lg"
              className="bg-accent text-accent-foreground font-bold px-8 sm:px-10 md:px-12 py-6 sm:py-7 md:py-8 rounded-full text-base sm:text-lg md:text-xl shadow-xl shadow-accent/40"
              onClick={async () => {
                try {
                  const res = await createBioDraft({});
                  if (res.status === 401) {
                    window.location.href = "/sign-in";
                    return;
                  }
                  if (!res.ok) return;
                  const data = await res.json();
                  if (data?.id)
                    window.location.href = `/editor/${encodeURIComponent(
                      data.id,
                    )}`;
                } catch {
                  // ignore network errors
                }
              }}
            >
              get started for free ♡
            </Button>
          </motion.div>
        </motion.div>
      </section>

      <motion.footer
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="relative z-10 py-12 sm:py-16 px-4 sm:px-6 border-t border-border/40"
      >
        <div className="max-w-6xl mx-auto text-center text-foreground/60">
          <p className="mb-3 sm:mb-4 text-sm sm:text-base">
            made with ♡ by the clove.moe team
          </p>
          <div className="flex items-center justify-center gap-4 sm:gap-6 text-sm sm:text-base">
            <a href="/docs" className="hover:text-accent transition-colors">
              docs
            </a>
            <a href="/terms" className="hover:text-accent transition-colors">
              terms
            </a>
            <a href="/privacy" className="hover:text-accent transition-colors">
              privacy
            </a>
            <a
              href="https://discord.gg/emrucgSEq2"
              className="hover:text-accent transition-colors"
            >
              discord
            </a>
          </div>
        </div>
      </motion.footer>
    </div>
  );
}
